import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import type { FormEvent } from 'react';

import {
  createAsset,
  productQuery,
  productsQuery,
  setAssetStatus,
  installedGearQuery,
  setInstalledRooms,
  updateAsset,
  SETTABLE_STATUSES,
  STATUS_LABELS,
  type Asset,
  type NewAsset,
  type SettableStatus,
} from '../api/inventory.ts';
import { LINE_TYPE_LABELS } from '../api/agreements.ts';
import { rolesQuery } from '../api/crew.ts';
import { meQuery } from '../api/me.ts';
import { Button } from '../components/button.tsx';
import { AddButton, EditButton } from '../components/action-buttons.tsx';
import { roomsQuery, venuesQuery } from '../api/venues.ts';
import { PageHeader } from '../components/page-header.tsx';
import { AssetForm, ProductForm } from './product-forms.tsx';
import { RatesSection } from './product-rates.tsx';
import { InstalledSection } from './installed-section.tsx';
import { StockByLocation } from './stock-moves.tsx';
import { dollarsToMinorUnits, formatMinorUnits } from '../money.ts';

// One product and its serials (CB-REQ INV-A01 – INV-A03, v1.940). Status changes offer only the
// states this module writes; staged and on a job arrive with book-out (WHS-16), and the database
// refuses a move INV-A03 does not permit however it is asked.

export function ProductScreen() {
  const { productId } = useParams({ from: '/authenticated/inventory/$productId' });
  const product = useQuery(productQuery(productId));
  const me = useQuery(meQuery);
  const [adding, setAdding] = useState(false);
  const [editingProduct, setEditingProduct] = useState(false);
  const [editing, setEditing] = useState<Asset | null>(null);

  const mayManage = me.data?.capabilities.includes('inventory.manage') ?? false;
  const seesCosts = me.data?.capabilities.includes('inventory.view-costs') ?? false;
  const mayMove = me.data?.capabilities.includes('inventory.move') ?? false;
  const mayAdjust = me.data?.capabilities.includes('inventory.adjust') ?? false;
  const venues = useQuery(venuesQuery);
  const roles = useQuery({ ...rolesQuery, enabled: !!product.data?.crewRoleId });

  if (product.isPending) return <p className="text-muted">Loading…</p>;
  if (product.isError) {
    return (
      <p role="alert" className="alert">
        {product.error.message}
      </p>
    );
  }
  const p = product.data;
  const retired = p.assets.filter((a) => a.status === 'retired').length;
  const venueName = venues.data?.find((v) => v.id === p.ownerVenueId)?.name;

  return (
    <div className="flex flex-col gap-6">
      <Link to="/inventory" className="link self-start text-sm">
        ← Catalogue
      </Link>
      <PageHeader
        kicker={p.category ? `Inventory · ${p.category.name}` : 'Inventory'}
        title={p.name}
        meta={
          <>
            {p.ownerVenueId ? (
              <span className="tag mr-2">VENUE EQUIP · {venueName ?? 'a venue'}</span>
            ) : null}
            {[p.manufacturer, p.model].filter(Boolean).join(' ') || 'No manufacturer recorded'}
          </>
        }
        actions={
          mayManage ? (
            <>
              {!editingProduct ? (
                <EditButton what="product" onClick={() => setEditingProduct(true)} />
              ) : null}
              {p.tracking === 'serialised' && !adding ? (
                <AddButton
                  noun="Unit"
                  onClick={() => {
                    setEditing(null);
                    setAdding(true);
                  }}
                />
              ) : null}
            </>
          ) : null
        }
      />

      {editingProduct ? (
        <ProductForm product={p} seesCosts={seesCosts} onDone={() => setEditingProduct(false)} />
      ) : null}

      <dl className="stat-grid grid-cols-2 md:grid-cols-4">
        <div className="stat">
          <dt className="kicker">On hand</dt>
          <dd className="stat-value">{p.stockOnHand}</dd>
        </div>
        <div className="stat">
          <dt className="kicker">Counted by</dt>
          <dd className="mt-1.5 text-lg font-extrabold">
            {p.tracking === 'serialised'
              ? 'Serial or tag'
              : p.tracking === 'bulk'
                ? 'Quantity'
                : 'Service — no stock'}
          </dd>
        </div>
        <div className="stat">
          <dt className="kicker">Line type</dt>
          <dd className="mt-1.5 text-lg font-extrabold">{LINE_TYPE_LABELS[p.lineType]}</dd>
        </div>
        {/* OPS-01 (crew part 4A): the role its lines' tasks ask for. */}
        {p.crewRoleId ? (
          <div className="stat">
            <dt className="kicker">Crew role</dt>
            <dd className="mt-1.5 text-lg font-extrabold">
              {roles.data?.find((r) => r.id === p.crewRoleId)?.name ?? '…'}
            </dd>
          </div>
        ) : null}
        {p.tracking === 'serialised' ? (
          <div className="stat">
            <dt className="kicker">Units recorded</dt>
            <dd className="stat-value">{p.assets.length}</dd>
            {retired > 0 ? <dd className="mt-1 text-xs text-muted">{retired} retired</dd> : null}
          </div>
        ) : null}
        {p.replacementCost ? (
          <div className="stat">
            <dt className="kicker">Replacement cost</dt>
            <dd className="mt-1.5 text-lg font-extrabold tabular-nums">
              {formatMinorUnits(p.replacementCost.amountMinor, p.replacementCost.currency)}
            </dd>
          </div>
        ) : null}
      </dl>

      {/* PRC-02 / PRC-03: above the price line, so only asked for by someone who can see prices.
          INV-A14 at v1.989: the venue's own gear takes rates too, charged beyond what its hire
          includes on a resale or direct-bill job (v1.990), sold for the venue. */}
      {me.data?.canSeePrices ? (
        <RatesSection productId={p.id} mayManage={me.data.capabilities.includes('prices.manage')} />
      ) : null}
      {p.ownerVenueId ? (
        <p className="text-ink-soft">
          The venue’s own: free where its hire includes it. Beyond that, charged at its rate on a
          resale or direct-bill job.
        </p>
      ) : null}

      {p.tracking !== 'service' ? (
        <StockByLocation product={p} mayMove={mayMove} mayAdjust={mayAdjust} />
      ) : null}

      {/* VEN-03 at v1.986: where a venue's own item is installed. */}
      {p.ownerVenueId && p.tracking !== 'service' ? (
        <ProductInstalled
          productId={p.id}
          venueId={p.ownerVenueId}
          tracking={p.tracking}
          units={p.assets.map((a) => ({
            id: a.id,
            label: a.assetTag ?? a.serialNumber ?? 'Unit',
            installedRoomId: a.installedRoomId ?? null,
          }))}
          onHand={p.stockOnHand}
          counts={p.installedRooms}
          mayManage={mayManage}
        />
      ) : null}

      {p.tracking === 'service' ? (
        <p className="text-ink-soft">
          A service: it is sold rather than hired, so it holds no stock and never appears in
          availability. Its price is its rate above.
        </p>
      ) : p.tracking === 'bulk' ? (
        <p className="text-ink-soft">
          Counted in bulk, so it has no individual units: its stock is a count at each location.
        </p>
      ) : (
        <section className="flex flex-col gap-4" aria-labelledby="units">
          <h2 id="units" className="section-title">
            Units
          </h2>
          {adding ? (
            <AssetForm productId={p.id} seesCosts={seesCosts} onDone={() => setAdding(false)} />
          ) : null}
          {editing ? (
            <AssetForm
              // Keyed, so choosing another unit starts its form afresh rather than keeping edits.
              key={editing.id}
              productId={p.id}
              asset={editing}
              seesCosts={seesCosts}
              onDone={() => setEditing(null)}
            />
          ) : null}
          {p.assets.length === 0 ? (
            <p className="text-ink-soft">No units recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <caption className="sr-only">Units of {p.name}, with their status</caption>
                <thead>
                  <tr>
                    <th scope="col">Tag</th>
                    <th scope="col">Serial</th>
                    <th scope="col">Location</th>
                    <th scope="col">Bought</th>
                    <th scope="col">Status</th>
                    {mayManage ? (
                      <th scope="col">
                        <span className="sr-only">Actions</span>
                      </th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {p.assets.map((a) => (
                    <AssetRow
                      key={a.id}
                      asset={a}
                      productId={p.id}
                      mayManage={mayManage}
                      onEdit={() => {
                        setAdding(false);
                        setEditing(a);
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function AssetRow({
  asset,
  productId,
  mayManage,
  onEdit,
}: {
  asset: Asset;
  productId: string;
  mayManage: boolean;
  onEdit: () => void;
}) {
  const queryClient = useQueryClient();
  const change = useMutation({
    mutationFn: (status: SettableStatus) => setAssetStatus(asset.id, status),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: productQuery(productId).queryKey });
      await queryClient.invalidateQueries({ queryKey: productsQuery.queryKey });
    },
  });
  const settable = (SETTABLE_STATUSES as readonly string[]).includes(asset.status);
  const label = asset.assetTag ?? asset.serialNumber ?? 'unit';

  return (
    <tr className={asset.status === 'retired' ? 'text-muted' : undefined}>
      <th scope="row" className="font-mono font-semibold">
        {asset.assetTag ?? '—'}
      </th>
      <td className="font-mono text-sm">{asset.serialNumber ?? '—'}</td>
      <td className="text-sm">{asset.location.name}</td>
      <td className="text-sm">
        {asset.purchasedOn ?? '—'}
        {/* Present only above the price line (INV-A01); the API decides, not this screen. */}
        {asset.purchasePrice ? (
          <span className="block text-muted tabular-nums">
            {formatMinorUnits(asset.purchasePrice.amountMinor, asset.purchasePrice.currency)}
            {asset.supplier ? ` · ${asset.supplier}` : ''}
          </span>
        ) : null}
      </td>
      <td>
        {mayManage && settable && asset.status !== 'retired' ? (
          <label>
            <span className="sr-only">Status of {label}</span>
            <select
              value={asset.status}
              disabled={change.isPending}
              onChange={(e) => change.mutate(e.currentTarget.value as SettableStatus)}
              className="input py-1"
            >
              {SETTABLE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <span className={asset.status === 'in_stock' ? 'tag' : 'tag tag-accent-2'}>
            {STATUS_LABELS[asset.status]}
          </span>
        )}
        {change.isError ? (
          <p role="alert" className="mt-1 text-sm text-accent-2-700">
            {change.error.message}
          </p>
        ) : null}
      </td>
      {mayManage ? (
        <td className="text-right">
          <EditButton what={label} onClick={onEdit} />
        </td>
      ) : null}
    </tr>
  );
}

/** VEN-03 at v1.986: the Installed section, fed from the API. */
function ProductInstalled({
  productId,
  venueId,
  tracking,
  units,
  onHand,
  counts,
  mayManage,
}: {
  productId: string;
  venueId: string;
  tracking: 'serialised' | 'bulk' | 'service';
  units: ReadonlyArray<{ id: string; label: string; installedRoomId: string | null }>;
  onHand: number;
  counts: ReadonlyArray<{ roomId: string; quantity: number }>;
  mayManage: boolean;
}) {
  const rooms = useQuery(roomsQuery(venueId));
  const queryClient = useQueryClient();
  // The product, and the room cards that list what is installed where.
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: productQuery(productId).queryKey }),
      queryClient.invalidateQueries({ queryKey: installedGearQuery(venueId).queryKey }),
    ]);
  const unit = useMutation({
    mutationFn: ({ assetId, roomId }: { assetId: string; roomId: string | null }) =>
      updateAsset(assetId, { installedRoomId: roomId }),
    onSuccess: refresh,
  });
  const split = useMutation({
    mutationFn: (next: Array<{ roomId: string; quantity: number }>) =>
      setInstalledRooms(productId, next),
    onSuccess: refresh,
  });
  if (!rooms.data) return null;
  return (
    <>
      <InstalledSection
        tracking={tracking}
        units={units}
        onHand={onHand}
        counts={counts}
        rooms={rooms.data}
        mayManage={mayManage}
        onSetUnit={(assetId, roomId) => unit.mutateAsync({ assetId, roomId })}
        onSetCounts={(next) => split.mutateAsync(next)}
      />
      {unit.error || split.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {(unit.error ?? split.error)!.message}
        </p>
      ) : null}
    </>
  );
}
